import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Loader2, UserPlus } from "lucide-react";

export default function UsuariosConfig() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [agencias, setAgencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nombre: "", email: "", password: "", rol: "operador", agencia_id: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, a] = await Promise.all([api.get("/users"), api.get("/agencias")]);
      setUsers(u.data);
      setAgencias(a.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!form.nombre.trim() || !form.email.trim() || form.password.length < 8) {
      toast.error("Completá nombre, email y una contraseña de al menos 8 caracteres.");
      return;
    }
    if (form.rol === "agencia" && !form.agencia_id) {
      toast.error("Elegí la agencia a la que se vincula este usuario.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.rol !== "agencia") delete payload.agencia_id;
      await api.post("/users", payload);
      toast.success("Usuario creado. Deberá cambiar su contraseña en el primer ingreso.");
      setOpen(false);
      setForm({ nombre: "", email: "", password: "", rol: "operador", agencia_id: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActivo = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { activo: !u.activo });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const changeRol = async (u, rol) => {
    if (rol === "agencia" && !u.agencia_id) {
      toast.error("Para vincular una agencia, creá el usuario desde 'Nuevo usuario'.");
      return;
    }
    try {
      await api.put(`/users/${u.id}`, { rol });
      toast.success("Rol actualizado.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const ROL_LABEL = { admin: "Administrador", operador: "Operador", agencia: "Agencia" };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Usuarios del equipo de operaciones.</p>
        <Button onClick={() => setOpen(true)} data-testid="new-user-button" className="gap-2">
          <Plus className="h-4 w-4" /> Nuevo usuario
        </Button>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Nombre</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Rol</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Agencia</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Activo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400 inline" />
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50">
                    <TableCell className="font-medium text-slate-900">
                      {u.nombre}
                      {u.id === me?.id && <span className="text-[11px] text-slate-400 ml-2">(vos)</span>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 font-mono">
                      {u.email}
                      {u.temp_password && u.must_change_password && (
                        <div className="mt-1 inline-flex items-center gap-1 rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[11px] text-amber-800" data-testid={`user-temp-pwd-${u.id}`}>
                          Clave temporal: <span className="font-semibold select-all">{u.temp_password}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={u.rol}
                        onValueChange={(v) => changeRol(u, v)}
                        disabled={u.id === me?.id}
                      >
                        <SelectTrigger className="w-36 h-9" data-testid={`user-rol-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Administrador</SelectItem>
                          <SelectItem value="operador">Operador</SelectItem>
                          <SelectItem value="agencia">Agencia</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600" data-testid={`user-agencia-${u.id}`}>
                      {u.rol === "agencia" ? (u.agencia_nombre || <span className="text-amber-600">sin vincular</span>) : <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={u.activo}
                        onCheckedChange={() => toggleActivo(u)}
                        disabled={u.id === me?.id}
                        data-testid={`user-activo-${u.id}`}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserPlus className="h-5 w-5" /> Nuevo usuario
            </DialogTitle>
            <DialogDescription>
              Creá una cuenta para el equipo. El usuario deberá cambiar la contraseña en su primer ingreso.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                data-testid="user-nombre-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Contraseña temporal</Label>
              <Input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Mínimo 8 caracteres"
                data-testid="user-password-input"
              />
              <p className="text-xs text-slate-400">El usuario deberá cambiarla en su primer ingreso.</p>
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select value={form.rol} onValueChange={(v) => setForm({ ...form, rol: v })}>
                <SelectTrigger data-testid="user-rol-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operador">Operador</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="agencia">Agencia (portal externo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.rol === "agencia" && (
              <div className="space-y-2">
                <Label>Agencia vinculada</Label>
                <Select value={form.agencia_id} onValueChange={(v) => setForm({ ...form, agencia_id: v })}>
                  <SelectTrigger data-testid="user-agencia-select">
                    <SelectValue placeholder="Elegí una agencia" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {agencias.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">El usuario verá solo la disponibilidad y las reservas de esta agencia.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="user-form-cancel">
              Cancelar
            </Button>
            <Button onClick={create} disabled={saving} data-testid="user-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear usuario"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
